import React from 'react'

import { useTranslation } from '@mc/i18n'
import { usePromotion } from '@mc/promo-utils'
import { EnhancedCtaCopy, usePrimaryCta } from '@mc/growth'
import Experiment from '@mc/experiments-react'
import { trackPurchaseClick } from '@mc/pre-paywall-events'
import { getPageSource, NavigationEvents, track } from '@mc/track-event'
import { Button } from '@mc/design-system'

import { GiftButton, UrgencyTextGreen } from '../../components'
import { useGiftEnabled } from '../../lib/helpers'
import { GLOBAL_CTAS_STICKY_FOOTER_I18N_NAMESPACE } from './i18n'

import classes from './GiftButton.module.scss'
import { StickyFooterContentTitle } from '../../components/StickyFooterContent/StickyFooterContentTitle'
import { GLOBAL_CTAS_STICKY_FOOTER_FLAG_NAME } from './constants'

const LOCATION = 'sticky_footer'

interface GlobalCtaExperimentProps {
  Control: () => React.ReactNode
  SignUpCta: React.ComponentType<{ fullWidth?: boolean; className?: string }>
  PricingText: React.ComponentType
}

export const GlobalCtaStickyFooterExperiment = ({
  Control,
  SignUpCta,
  PricingText,
}: GlobalCtaExperimentProps) => {
  const { giftEnabled } = useGiftEnabled()
  const { isPromotionOn } = usePromotion()

  if (!giftEnabled || !isPromotionOn) return <Control />

  return (
    <Experiment
      name={GLOBAL_CTAS_STICKY_FOOTER_FLAG_NAME}
      variant_1={(props: Variant1Props) => (
        <Variant1 SignUpCta={props.SignUpCta} PricingText={props.PricingText} />
      )}
      variant_2={(props: Variant2Props) => (
        <Variant2 PricingText={props.PricingText} />
      )}
      control={Control}
      SignUpCta={SignUpCta}
      PricingText={PricingText}
    />
  )
}

type Variant1Props = Omit<GlobalCtaExperimentProps, 'Control'>

const Variant1 = ({ SignUpCta, PricingText }: Variant1Props) => (
  <div className='container mc-p-4'>
    {/* Desktop */}
    <div className='d-none d-sm-flex align-items-center justify-content-between'>
      <div className='d-flex flex-wrap align-items-center'>
        <StickyFooterContentTitle className='mc-mr-sm-4' />
        <p className='mc-text-small'>
          <UrgencyTextGreen />
        </p>
      </div>
      <div className='d-flex align-items-center mc-ml-2'>
        <p className='mc-text-h7 mc-mr-2 mc-mr-md-3'>
          <PricingText />
        </p>
        <div
          className={`d-flex flex-gap-2 align-items-end ${classes.invertedTertiary}`}
        >
          <GiftButton className='mc-mr-2' location={LOCATION} />
        </div>
        <SignUpCta />
      </div>
    </div>

    {/* Mobile */}
    <div className='d-flex d-sm-none flex-column mc-gap-3'>
      <div>
        <div className='mc-mb-2'>
          <StickyFooterContentTitle />
        </div>
        <p className='mc-font-caption'>
          <PricingText />
          {`. `}
          <UrgencyTextGreen />
        </p>
      </div>

      <div className='row'>
        <div className={`${classes.invertedTertiary} col-6`}>
          <GiftButton
            className='mc-mr-2 mc-py-2'
            location={LOCATION}
            fullWidth
          />
        </div>
        <div className='col-6'>
          <SignUpCta fullWidth className='mc-py-2' />
        </div>
      </div>
    </div>
  </div>
)

type Variant2Props = Omit<GlobalCtaExperimentProps, 'Control' | 'SignUpCta'>

const Variant2 = ({ PricingText }: Variant2Props) => {
  const { t } = useTranslation(GLOBAL_CTAS_STICKY_FOOTER_I18N_NAMESPACE)
  const ctaVariant2 = t('ctaVariant2')
  const { ctaHref } = usePrimaryCta()

  const onCtaClick = () => {
    trackPurchaseClick({
      action: ctaVariant2,
      href: window.location.href,
      location: LOCATION,
      pageSource: getPageSource(),
    })
    // Same track as StickyFooter
    track(NavigationEvents.EVENTS.NAVIGATION_CLICKED, {
      action: ctaVariant2,
      location: LOCATION,
      page_source: getPageSource(),
    })
  }
  return (
    <div className='container mc-p-4'>
      {/* Desktop */}
      <div className='d-none d-sm-flex align-items-center justify-content-between'>
        <div className='d-flex flex-wrap align-items-center'>
          <StickyFooterContentTitle />
          <p className='mc-text-small'>
            <UrgencyTextGreen />
          </p>
        </div>
        <div className='d-flex align-items-center mc-ml-2'>
          <p className='mc-text-h7 mc-mr-2 mc-mr-md-4'>
            <PricingText />
          </p>
          <Button as='a' kind='play' href={ctaHref} onClick={onCtaClick}>
            <EnhancedCtaCopy size='sm'>{ctaVariant2}</EnhancedCtaCopy>
          </Button>
        </div>
      </div>

      {/* Mobile */}
      <div className='d-flex d-sm-none flex-column mc-gap-3'>
        <div className='d-flex align-items-center justify-content-between mc-gap-4'>
          <StickyFooterContentTitle />
          <Button as='a' kind='play' href={ctaHref} onClick={onCtaClick}>
            <EnhancedCtaCopy size='sm'>{ctaVariant2}</EnhancedCtaCopy>
          </Button>
        </div>
        <div
          className={`d-flex justify-content-between mc-gap-4 ${classes.bottomContainer}`}
        >
          <p
            className='mc-font-caption align-self-end'
            style={{ minWidth: '33%' }}
          >
            <UrgencyTextGreen />
          </p>
          <p className='mc-font-caption mc-text--right'>
            <PricingText />
          </p>
        </div>
      </div>
    </div>
  )
}
